import { Fragment, useState, useEffect } from "react";
import { Typography } from "@mui/material";
import ScoringSnapshotDataGridPro from "./scoringSnapshotDataGridPro";
import {
    ScoringSnapshotResponse,
    ScoringSnapshotItem,
} from "../../services/types/scoringStatusTypes";
import CclAlertSnackbar from "../../components/common/cclAlertSnackbar";
import {
    useLazyScoringSnapshotBySessionQuery,
    useLazyScoringSnapshotByParticipantQuery,
    useScoringSnapshotByDateRangeMutation,
} from "../../services/cclTokenedAssessmentApi";

export interface ScoringSnapshotProps {
    fromSessionDetail?: boolean;
    sessionId?: string;
    fromParticipantDetail?: boolean;
    esiKey?: number;
}

export const ScoringSnapshot: React.FC<ScoringSnapshotProps> = (props) => {
    const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
    const [snapshotItems, setSnapshotItems] = useState<ScoringSnapshotItem[]>([]);
    const [getScoringSnapshotByDateRange, { data: dateRangeSnapshot, isLoading: dateRangeLoading }] =
        useScoringSnapshotByDateRangeMutation();
    const [getSessionScoringHistory, { data: sessionScoringHistory, isLoading: sessionLoading, isFetching: sessionFetching }] =
        useLazyScoringSnapshotBySessionQuery();
    const [getParticipantScoringHistory, { data: participantScoringHistory, isLoading: participantLoading, isFetching: participantFetching }] =
        useLazyScoringSnapshotByParticipantQuery();

    useEffect(() => {
        refreshSnapshot(false);
        //eslint-disable-next-line
    }, []);

    const refreshSnapshot = (manual: boolean) => {
        if (props.fromParticipantDetail && props.esiKey != null) {
            getParticipantScoringHistory(props.esiKey)
                .unwrap()
                .then((snapshot: ScoringSnapshotResponse) => {
                    setSnapshotItems([...snapshot.items]);
                    if (manual) setShowSnackbar(true);
                })
                .catch(() => {});
            return;
        }
        if (props.fromSessionDetail && props.sessionId) {
            getSessionScoringHistory(props.sessionId)
                .unwrap()
                .then((snapshot: ScoringSnapshotResponse) => {
                    setSnapshotItems([...snapshot.items]);
                    if (manual) setShowSnackbar(true);
                })
                .catch(() => {});
            return;
        }
        const now = new Date();
        const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6, 0, 0, 0, 0);
        const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 0);
        getScoringSnapshotByDateRange({
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
        })
            .unwrap()
            .then((snapshot: ScoringSnapshotResponse) => {
                setSnapshotItems([...snapshot.items]);
                if (manual) setShowSnackbar(true);
            })
            .catch(() => {});
    };

    let data = snapshotItems;

    if (props.fromSessionDetail && snapshotItems.length > 0 && props.sessionId !== undefined) {
        data = snapshotItems.filter((item) => item.programId === +props.sessionId!);
    }

    const anyLoading = dateRangeLoading || sessionLoading || participantLoading;
    const hasData = dateRangeSnapshot != null || sessionScoringHistory != null || participantScoringHistory != null;
    const isFetchingAny = sessionFetching || participantFetching;

    if (anyLoading || hasData) {
        return (
            <Fragment>
                {showSnackbar ? (
                    <CclAlertSnackbar
                        open={true}
                        onClose={() => setShowSnackbar(false)}
                        message={"Data Refreshed"}
                        severity={"success"}
                    />
                ) : null}
                <ScoringSnapshotDataGridPro
                    loading={anyLoading || isFetchingAny}
                    snapshotItems={data}
                    refresh={refreshSnapshot}
                    fromSessionDetail={props.fromSessionDetail}
                    fromParticipantDetail={props.fromParticipantDetail}
                />
            </Fragment>
        );
    }

    return <Typography>Error loading page</Typography>;
};

export default ScoringSnapshot;
